import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader, Mail, ArrowRight, RefreshCw } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';

export default function VerifyEmailPage() {
  const navigate       = useNavigate();
  const [params]       = useSearchParams();
  const token          = params.get('token');
  const [status, setStatus] = useState('verifying'); // verifying | success | error | resend
  const [error, setError]   = useState('');
  const [email, setEmail]   = useState('');
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!token) { setStatus('resend'); return; }
    api.post('/auth/verify-email', { token })
      .then(() => setStatus('success'))
      .catch(err => {
        setError(err.response?.data?.message || 'Verification failed');
        setStatus('error');
      });
  }, [token]);

  const resend = async e => {
    e.preventDefault();
    if (!email.trim()) { toast.error('Enter your email'); return; }
    setResending(true);
    try {
      await api.post('/auth/resend-verification', { email: email.trim().toLowerCase() });
      toast.success('New verification email sent!');
      setEmail('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send');
    } finally { setResending(false); }
  };

  return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
      <div style={{ width:'100%', maxWidth:440 }}>

        {/* Logo */}
        <div style={{ textAlign:'center', marginBottom:32 }}>
          <div style={{ display:'inline-flex', alignItems:'center', gap:9, fontFamily:'var(--font-display)', fontSize:22, fontWeight:800 }}>
            <div style={{ width:38, height:38, background:'linear-gradient(135deg,var(--accent),var(--accent2))', borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18 }}>📡</div>
            Lead<span style={{ color:'var(--accent)' }}>Radar</span>
          </div>
        </div>

        <div className="card" style={{ padding:36, textAlign:'center' }}>

          {/* Verifying */}
          {status === 'verifying' && (
            <>
              <div style={{ width:72, height:72, background:'var(--bg3)', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px' }}>
                <Loader size={32} style={{ color:'var(--accent)', animation:'spin 1s linear infinite' }}/>
              </div>
              <h2 style={{ fontFamily:'var(--font-display)', fontSize:22, fontWeight:800, marginBottom:8 }}>Verifying your email…</h2>
              <p style={{ color:'var(--text3)', fontSize:14 }}>Please wait a moment.</p>
            </>
          )}

          {/* Success */}
          {status === 'success' && (
            <>
              <div style={{ width:72, height:72, background:'var(--green-bg)', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px' }}>
                <CheckCircle2 size={36} style={{ color:'var(--green)' }}/>
              </div>
              <h2 style={{ fontFamily:'var(--font-display)', fontSize:24, fontWeight:800, marginBottom:10 }}>Email Verified! 🎉</h2>
              <p style={{ color:'var(--text3)', fontSize:14, lineHeight:1.7, marginBottom:28 }}>
                Your LeadRadar account is now active. You can sign in and start finding leads.
              </p>
              <button className="btn btn-primary btn-full btn-lg" onClick={() => navigate('/login')}>
                <ArrowRight size={16}/> Sign In Now
              </button>
            </>
          )}

          {/* Error */}
          {status === 'error' && (
            <>
              <div style={{ width:72, height:72, background:'var(--red-bg)', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px' }}>
                <XCircle size={36} style={{ color:'var(--red)' }}/>
              </div>
              <h2 style={{ fontFamily:'var(--font-display)', fontSize:22, fontWeight:800, marginBottom:10 }}>Verification Failed</h2>
              <p style={{ color:'var(--text3)', fontSize:14, lineHeight:1.7, marginBottom:24 }}>{error}</p>
              <button className="btn btn-ghost btn-full" onClick={() => setStatus('resend')} style={{ marginBottom:12 }}>
                <RefreshCw size={14}/> Request New Link
              </button>
              <Link to="/login" style={{ fontSize:13.5, color:'var(--accent)', fontWeight:600 }}>← Back to Sign In</Link>
            </>
          )}

          {/* Resend form */}
          {status === 'resend' && (
            <>
              <div style={{ width:72, height:72, background:'var(--accent-bg)', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px' }}>
                <Mail size={32} style={{ color:'var(--accent)' }}/>
              </div>
              <h2 style={{ fontFamily:'var(--font-display)', fontSize:22, fontWeight:800, marginBottom:10 }}>Verify Your Email</h2>
              <p style={{ color:'var(--text3)', fontSize:14, lineHeight:1.7, marginBottom:24 }}>
                Enter your email address and we'll send you a new verification link.
              </p>
              <form onSubmit={resend} style={{ display:'flex', flexDirection:'column', gap:14, textAlign:'left' }}>
                <div className="form-group">
                  <label className="form-label">Email Address</label>
                  <div className="input-group">
                    <Mail size={14} className="input-icon"/>
                    <input className="input" type="email" placeholder="you@company.com"
                      value={email} onChange={e => setEmail(e.target.value)} required autoFocus/>
                  </div>
                </div>
                <button className="btn btn-primary btn-full" type="submit" disabled={resending}>
                  {resending ? <><div className="spinner spinner-sm"/> Sending…</> : 'Send Verification Link →'}
                </button>
              </form>
              <p style={{ textAlign:'center', marginTop:20, fontSize:13.5, color:'var(--text3)' }}>
                Already verified? <Link to="/login" style={{ color:'var(--accent)', fontWeight:600 }}>Sign In</Link>
              </p>
            </>
          )}

        </div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}